import { Configuration, fetchOptions, OptionItem } from "@interface48/api";
import Input from "@material-ui/core/Input";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import classNames from "classnames";
import React from "react";
import ReactAutosuggest, {
  ChangeEvent,
  SuggestionSelectedEventData,
  SuggestionsFetchRequestedParams,
} from "react-autosuggest";
import { AutosuggestRemoteOptions } from "./models";

const styles = (theme: Theme) =>
  createStyles({
    container: {
      flexGrow: 1,
      position: "relative",
    },
    suggestionsContainerOpen: {
      position: "absolute",
      marginBottom: theme.spacing(3),
      left: 0,
      right: 0,
      zIndex: 1,
    },
    suggestion: {
      display: "block",
    },
    suggestionsList: {
      margin: 0,
      padding: 0,
      listStyleType: "none",
    },
    textField: {
      marginTop: theme.spacing(2),
      width: "100%",
    },
  });

const renderInput = (inputProps: any) => {
  // Discard placeholder, as we're using a shrinkable label instead
  const { classes, value, ref, placeholder, ...other } = inputProps;

  return <Input className={classes.textField} value={value} inputRef={ref} {...other} />;
};

const renderSuggestion = (suggestion: any, { query, isHighlighted }: { query: any; isHighlighted: boolean }) => {
  const matches = match(suggestion.label, query);
  const parts = parse(suggestion.label, matches);

  return (
    <MenuItem selected={isHighlighted} component="div">
      <div>
        {parts.map((part: { highlight: any; text: string }, index: number) => {
          return part.highlight ? (
            <span key={index} style={{ fontWeight: 500 }}>
              {part.text}
            </span>
          ) : (
            <strong key={index} style={{ fontWeight: 300 }}>
              {part.text}
            </strong>
          );
        })}
      </div>
    </MenuItem>
  );
};

const renderSuggestionsContainer = (options: any) => {
  const { containerProps, children } = options;

  return (
    <Paper {...containerProps} square={true}>
      {children}
    </Paper>
  );
};

const getSuggestionValue = (option: OptionItem<string>) => {
  return option.value;
};

const getSuggestions = (options: Array<OptionItem<string>>, value: string) => {
  const inputValue = value.trim().toLowerCase();
  const inputLength = inputValue.length;
  let count = 0;

  return inputLength === 0
    ? []
    : options
        .filter(option => {
          const keep = count < 10 && option.label.toLowerCase().indexOf(inputValue) !== -1;

          if (keep) {
            count += 1;
          }

          return keep;
        })
        .sort((aOption, bOption) => aOption.label.localeCompare(bOption.label));
};

interface AutosuggestProps extends WithStyles<typeof styles> {
  id: string;
  label?: string;
  placeholder?: string;
  value: string | null | undefined;
  options?: Array<OptionItem<string>>;
  remoteOptions?: AutosuggestRemoteOptions;
  className?: string;
  getApiConfiguration?: () => Promise<Configuration>;
  processValue?: (value: string | null | undefined) => string | null | undefined;
  onChange: (value: string | null | undefined, id?: string, otherData?: any) => void;
}

interface AutosuggestState {
  value?: string | null;
  optionItems: Array<OptionItem<string>>;
  suggestedOptionItems: Array<OptionItem<string>>;
}

export const AutosuggestComponent = class extends React.Component<AutosuggestProps, AutosuggestState> {
  constructor(props: AutosuggestProps) {
    super(props);

    const { value, options, remoteOptions } = this.props;

    // If a Remote URL has been specified, then options will be fetched when mounted
    const optionItems = remoteOptions ? [] : options || [];

    this.state = { value, optionItems, suggestedOptionItems: [] };
  }

  public async componentDidMount() {
    const { remoteOptions, getApiConfiguration } = this.props;
    const { value } = this.state;

    // If the Select has Remote Options...
    if (remoteOptions) {
      await this.loadRemoteOptions(value, remoteOptions, getApiConfiguration);
    }
  }

  public render() {
    const { id, placeholder, label, className, classes } = this.props;
    const { value, suggestedOptionItems } = this.state;

    return (
      <ReactAutosuggest
        theme={{
          container: classNames(classes.container, className),
          suggestionsContainerOpen: classes.suggestionsContainerOpen,
          suggestionsList: classes.suggestionsList,
          suggestion: classes.suggestion,
        }}
        renderInputComponent={renderInput}
        inputProps={
          {
            id,
            classes,
            label,
            placeholder,
            autoComplete: "off",
            value: value || "",
            onChange: this.handleChange,
            onBlur: this.handleBlur,
          } as any
        }
        suggestions={suggestedOptionItems}
        onSuggestionSelected={this.handleSuggestionSelected}
        onSuggestionsFetchRequested={this.handleSuggestionsFetchRequested}
        onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
        renderSuggestionsContainer={renderSuggestionsContainer}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
      />
    );
  }

  public async componentDidUpdate(prevProps: AutosuggestProps) {
    const { value: prevValue, options: prevOptions, remoteOptions: prevRemoteOptions } = prevProps;
    const { value, options, remoteOptions, getApiConfiguration } = this.props;

    // If the Options were updated via props...
    if (options && prevOptions !== options) {
      this.loadOptions(value, options);
    }
    // Otherwise, if the Select has Remote Options, check to see if the URL was updated via props...
    else if (prevRemoteOptions && remoteOptions && prevRemoteOptions.url !== remoteOptions.url) {
      this.loadRemoteOptions(value, remoteOptions, getApiConfiguration);
    }
    // Otherwise, if the value was updated via props...
    else if (prevValue !== value) {
      this.setState({ ...this.state, value });
    }
  }

  public handleSuggestionSelected = (event: React.FormEvent<any>, data: SuggestionSelectedEventData<any>) => {
    // Prevent form submission on enter to select value
    if (data.method === "enter") {
      event.preventDefault();
    }
  };

  public handleSuggestionsFetchRequested = (request: SuggestionsFetchRequestedParams) => {
    const { value } = request;
    const { optionItems } = this.state;

    this.setState((prevState: AutosuggestState) => {
      return { ...prevState, suggestedOptionItems: getSuggestions(optionItems, value) };
    });
  };

  public handleSuggestionsClearRequested = () => {
    this.setState((prevState: AutosuggestState) => {
      return { ...prevState, suggestedOptionItems: [] };
    });
  };

  public handleChange = (event: any, params: ChangeEvent) => {
    const { newValue } = params;

    this.setState((prevState: AutosuggestState) => {
      return { ...prevState, value: newValue };
    });
  };

  public handleBlur = (event: any, { highlightedSuggestion }: { highlightedSuggestion: OptionItem<string> }) => {
    const { onChange, processValue } = this.props;
    const { value } = this.state;

    if (highlightedSuggestion) {
      const suggestionValue = getSuggestionValue(highlightedSuggestion);
      onChange(suggestionValue);
    } else {
      const processedValue = processValue ? (processValue(value) as string | null | undefined) : value && value.trim();
      onChange(processedValue);
    }
  };

  public loadOptions = (value: string | null | undefined, options: Array<OptionItem<string>>) => {
    this.setState({ ...this.state, value, optionItems: options });
  };

  public loadRemoteOptions = async (
    value: string | null | undefined,
    remoteOptions: AutosuggestRemoteOptions,
    getApiConfiguration?: () => Promise<Configuration>,
  ) => {
    const { id, label } = this.props;

    const autosuggestRemoteOptions = remoteOptions as AutosuggestRemoteOptions;

    try {
      const options = await fetchOptions<string>(
        autosuggestRemoteOptions.url,
        "string", // Autosuggest only supports string values
        undefined, // Default Option Item not applicable for Autosuggest
        autosuggestRemoteOptions.sort,
        getApiConfiguration,
      );

      this.loadOptions(value, options);
    } catch (error) {
      console.error(error);

      alert(
        `An error occurred upon attempting to initialize the ${label || id} field.\n\n` +
          `Please reload the page and try again. If the error persists, please contact an Administrator for assistance.`,
      );
    }
  };
};

export const Autosuggest = withStyles(styles)(AutosuggestComponent);
